/**
 * トロフィーの定義と判定（Phaser 非依存・保存領域にも触らない純粋な関数）。
 *
 * 設計の元: プロジェクト文書「トロフィー・買い切り・アイテム設計」。
 *
 * - 色は難しさで分ける：銅 → 銀 → 金 → 白金
 * - 県ごとのメダルは「銅=Lv10 / 銀=Lv11 / 金=Lv12 / 白金=Lv12をコンティニューもおはしも使わずに」
 *   （12段の県の場合。段数が違う県は「最上位から数えて」で同じ意味にしている）
 * - コンティニューやおはしで届いた Lv12 も**金として認める**。腕前の証は「白金」だけが持つ（2026-10-04 に「極」から改名。トロフィーの最上位と呼び名をそろえた）
 * - 料理カテゴリ系は 100% ではなく **7割** で「制覇」。3割で「めぐり」
 */

import { FOOD_CATEGORIES, type FoodCategoryId } from '../game/config/foodCategories';
import { AREAS, type AreaId } from '../game/config/regions';

export type TrophyTier = 'bronze' | 'silver' | 'gold' | 'platinum';

export const TIER_LABEL: Record<TrophyTier, string> = {
  bronze: '銅',
  silver: '銀',
  gold: '金',
  platinum: '白金',
};

/** 県ごとのメダル。none < bronze < silver < gold < master。 */
export type PrefMedal = 'none' | 'bronze' | 'silver' | 'gold' | 'master';

export const MEDAL_RANK: Record<PrefMedal, number> = {
  none: 0,
  bronze: 1,
  silver: 2,
  gold: 3,
  master: 4,
};

export const MEDAL_LABEL: Record<PrefMedal, string> = {
  none: '—',
  bronze: '銅',
  silver: '銀',
  gold: '金',
  master: '白金',
};

/** 1県ぶんの進み具合（保存領域から集めたもの）。 */
export interface PrefProgress {
  regionId: string;
  regionName: string;
  areaId: AreaId | null;
  /** その県の段数（ふつう12）。 */
  maxLevel: number;
  /** アシスト込みの最高到達レベル。 */
  reached: number;
  /** コンティニューもおはしも使わずに届いた最高レベル。 */
  reachedNoAssist: number;
  unlockedItemIds: readonly string[];
  tipsFound: number;
  tipsTotal: number;
  playCount: number;
}

export interface MetaProgress {
  prefs: readonly PrefProgress[];
  /** カテゴリ -> 全国での収録数と図鑑で開いた数。 */
  categories: ReadonlyMap<FoodCategoryId, { total: number; found: number }>;
  /** 今日の県（いちばん長い連続日数・遊んだ日数）。 */
  daily?: { bestStreak: number; totalDays: number };
}

export function medalOf(p: Pick<PrefProgress, 'maxLevel' | 'reached' | 'reachedNoAssist'>): PrefMedal {
  const top = p.maxLevel;
  if (top <= 0) return 'none';
  if (p.reachedNoAssist >= top) return 'master';
  if (p.reached >= top) return 'gold';
  if (p.reached >= top - 1) return 'silver';
  if (p.reached >= top - 2) return 'bronze';
  return 'none';
}

/**
 * 結果画面に出す「次の目標」。今のメダルの一つ上までの距離を短い文で返す。
 * 白金まで取っていれば null。
 */
export function nextMedalGoal(p: Pick<PrefProgress, 'maxLevel' | 'reached' | 'reachedNoAssist'>): string | null {
  const top = p.maxLevel;
  if (top <= 2) return null;
  const medal = medalOf(p);
  const stepTo = (target: number, label: string) => {
    const gap = Math.max(1, target - p.reached);
    return `次の目標：${label}メダルまであと${gap}段（Lv${target}）`;
  };
  switch (medal) {
    case 'none':
      return stepTo(top - 2, '銅');
    case 'bronze':
      return stepTo(top - 1, '銀');
    case 'silver':
      return stepTo(top, '金');
    case 'gold':
      return `次の目標：白金メダル（コンティニューもおはしも使わずにLv${top}）`;
    default:
      return null;
  }
}

/** メダルの条件の説明（棚に出す）。 */
export function medalRequirement(maxLevel: number): Record<Exclude<PrefMedal, 'none'>, string> {
  return {
    bronze: `Lv${maxLevel - 2}に到達`,
    silver: `Lv${maxLevel - 1}に到達`,
    gold: `Lv${maxLevel}に到達（コンティニュー・おはし可）`,
    master: `Lv${maxLevel}に到達（コンティニューもおはしも使わずに）`,
  };
}

export type TrophyGroup =
  | 'start'
  | 'step'
  | 'daily'
  | 'medal'
  | 'area'
  | 'dex'
  | 'category'
  | 'tour'
  | 'play';

export const GROUP_LABEL: Record<TrophyGroup, string> = {
  start: 'はじめの一歩',
  step: 'つぎの一歩',
  daily: '今日の県',
  medal: 'メダルの数',
  area: '地方',
  dex: '図鑑',
  category: '料理の種類',
  tour: 'ご当地めぐり（豆知識）',
  play: 'あそんだ回数',
};

export interface TrophyDefinition {
  id: string;
  group: TrophyGroup;
  tier: TrophyTier;
  icon: string;
  name: string;
  /** 条件（未獲得のときも見せる。何をすればいいかが分かることが大事）。 */
  requirement: string;
  /** いまの値と目標値。current >= target で獲得。 */
  measure: (progress: MetaProgress) => { current: number; target: number };
}

const countMedals = (progress: MetaProgress, atLeast: PrefMedal) =>
  progress.prefs.filter((p) => MEDAL_RANK[medalOf(p)] >= MEDAL_RANK[atLeast]).length;

const totalDex = (progress: MetaProgress) =>
  progress.prefs.reduce((sum, p) => sum + p.unlockedItemIds.length, 0);
const totalDishes = (progress: MetaProgress) =>
  progress.prefs.reduce((sum, p) => sum + p.maxLevel, 0);
const totalTips = (progress: MetaProgress) =>
  progress.prefs.reduce((sum, p) => sum + p.tipsFound, 0);
const allTips = (progress: MetaProgress) =>
  progress.prefs.reduce((sum, p) => sum + p.tipsTotal, 0);
const totalPlays = (progress: MetaProgress) =>
  progress.prefs.reduce((sum, p) => sum + p.playCount, 0);

/** 目標値を「全体の何割」で決める（端数は切り上げ。全体が0なら到達不能にしない）。 */
export function ratioTarget(total: number, ratio: number): number {
  return Math.max(1, Math.ceil(total * ratio));
}

function fixed(current: number, target: number) {
  return { current: Math.min(current, target), target };
}

function build(): TrophyDefinition[] {
  const list: TrophyDefinition[] = [];

  /* ---- はじめの一歩 ---- */
  list.push(
    {
      id: 'first-play',
      group: 'start',
      tier: 'bronze',
      icon: '🥢',
      name: 'いただきます',
      requirement: 'どこかの県で1回あそぶ',
      measure: (p) => fixed(totalPlays(p), 1),
    },
    {
      id: 'first-bronze',
      group: 'start',
      tier: 'bronze',
      icon: '🥉',
      name: 'はじめての銅',
      requirement: 'どこかの県で銅メダル（Lv10）',
      measure: (p) => fixed(countMedals(p, 'bronze'), 1),
    },
    {
      id: 'first-silver',
      group: 'start',
      tier: 'silver',
      icon: '🥈',
      name: 'はじめての銀',
      requirement: 'どこかの県で銀メダル（Lv11）',
      measure: (p) => fixed(countMedals(p, 'silver'), 1),
    },
    {
      id: 'first-gold',
      group: 'start',
      tier: 'gold',
      icon: '🥇',
      name: 'はじめての頂点',
      requirement: 'どこかの県で金メダル（Lv12）',
      measure: (p) => fixed(countMedals(p, 'gold'), 1),
    },
    {
      id: 'first-master',
      group: 'start',
      tier: 'platinum',
      icon: '🏅',
      name: '腕一本',
      requirement: 'どこかの県で白金メダル（コンティニューもおはしも使わずにLv12）',
      measure: (p) => fixed(countMedals(p, 'master'), 1),
    },
  );

  /* ---- つぎの一歩（すぐ手が届く中間目標） ---- */
  const played = (p: MetaProgress) => p.prefs.filter((x) => x.playCount > 0).length;
  const areasPlayed = (p: MetaProgress) =>
    new Set(p.prefs.filter((x) => x.playCount > 0 && x.areaId).map((x) => x.areaId)).size;
  list.push(
    {
      id: 'played-3',
      group: 'step',
      tier: 'bronze',
      icon: '🧳',
      name: '寄り道',
      requirement: '3つの県であそぶ',
      measure: (p) => fixed(played(p), 3),
    },
    {
      id: 'bronze-3',
      group: 'step',
      tier: 'bronze',
      icon: '🥉',
      name: '三県の銅',
      requirement: '銅メダルを3県で',
      measure: (p) => fixed(countMedals(p, 'bronze'), 3),
    },
    {
      id: 'silver-3',
      group: 'step',
      tier: 'silver',
      icon: '🥈',
      name: '三県の銀',
      requirement: '銀メダルを3県で',
      measure: (p) => fixed(countMedals(p, 'silver'), 3),
    },
    {
      id: 'areas-6',
      group: 'step',
      tier: 'silver',
      icon: '🗺️',
      name: '北から南まで',
      requirement: '6つの地方で1県ずつあそぶ',
      measure: (p) => fixed(areasPlayed(p), AREAS.length),
    },
    {
      id: 'bronze-10',
      group: 'step',
      tier: 'silver',
      icon: '🥉',
      name: '十県めぐりの証',
      requirement: '銅メダルを10県で',
      measure: (p) => fixed(countMedals(p, 'bronze'), 10),
    },
  );

  /* ---- 今日の県 ---- */
  const dailyDays = (p: MetaProgress) => p.daily?.totalDays ?? 0;
  const dailyStreak = (p: MetaProgress) => p.daily?.bestStreak ?? 0;
  list.push(
    {
      id: 'daily-1',
      group: 'daily',
      tier: 'bronze',
      icon: '📅',
      name: '今日の一県',
      requirement: '今日の県であそぶ',
      measure: (p) => fixed(dailyDays(p), 1),
    },
    {
      id: 'daily-streak-3',
      group: 'daily',
      tier: 'bronze',
      icon: '🔥',
      name: '三日坊主こえ',
      requirement: '今日の県を3日つづけて',
      measure: (p) => fixed(dailyStreak(p), 3),
    },
    {
      id: 'daily-streak-7',
      group: 'daily',
      tier: 'silver',
      icon: '🔥',
      name: '一週間',
      requirement: '今日の県を7日つづけて',
      measure: (p) => fixed(dailyStreak(p), 7),
    },
    {
      id: 'daily-streak-30',
      group: 'daily',
      tier: 'gold',
      icon: '🔥',
      name: 'ひと月',
      requirement: '今日の県を30日つづけて',
      measure: (p) => fixed(dailyStreak(p), 30),
    },
    {
      id: 'daily-47',
      group: 'daily',
      tier: 'gold',
      icon: '🗾',
      name: '毎日が旅',
      requirement: '今日の県を47日あそぶ',
      measure: (p) => fixed(dailyDays(p), 47),
    },
  );

  /* ---- 県メダルの数 ---- */
  list.push(
    {
      id: 'gold-5',
      group: 'medal',
      tier: 'gold',
      icon: '🥇',
      name: '五県の頂点',
      requirement: '金メダルを5県で',
      measure: (p) => fixed(countMedals(p, 'gold'), 5),
    },
    {
      id: 'gold-20',
      group: 'medal',
      tier: 'platinum',
      icon: '🥇',
      name: '二十県の頂点',
      requirement: '金メダルを20県で',
      measure: (p) => fixed(countMedals(p, 'gold'), 20),
    },
    {
      id: 'all-bronze',
      group: 'medal',
      tier: 'gold',
      icon: '🗾',
      name: '全国行脚',
      requirement: 'すべての都道府県で銅メダル',
      measure: (p) => fixed(countMedals(p, 'bronze'), p.prefs.length),
    },
    {
      id: 'all-silver',
      group: 'medal',
      tier: 'platinum',
      icon: '🗾',
      name: '全国通',
      requirement: 'すべての都道府県で銀メダル',
      measure: (p) => fixed(countMedals(p, 'silver'), p.prefs.length),
    },
    {
      id: 'all-gold',
      group: 'medal',
      tier: 'platinum',
      icon: '👑',
      name: '全国制覇',
      requirement: 'すべての都道府県で金メダル',
      measure: (p) => fixed(countMedals(p, 'gold'), p.prefs.length),
    },
    {
      id: 'all-master',
      group: 'medal',
      tier: 'platinum',
      icon: '🏯',
      name: '日本一の腕',
      requirement: 'すべての都道府県で白金メダル',
      measure: (p) => fixed(countMedals(p, 'master'), p.prefs.length),
    },
  );

  /* ---- 地方 ---- */
  for (const area of AREAS) {
    const inArea = (p: MetaProgress) => p.prefs.filter((x) => x.areaId === area.id);
    const medalsIn = (p: MetaProgress, atLeast: PrefMedal) =>
      inArea(p).filter((x) => MEDAL_RANK[medalOf(x)] >= MEDAL_RANK[atLeast]).length;
    list.push(
      {
        id: `area-${area.id}-bronze`,
        group: 'area',
        tier: 'silver',
        icon: '🧭',
        name: `${area.name}めぐり`,
        requirement: `${area.name}のすべての県で銅メダル`,
        measure: (p) => fixed(medalsIn(p, 'bronze'), Math.max(1, inArea(p).length)),
      },
      {
        id: `area-${area.id}-gold`,
        group: 'area',
        tier: 'gold',
        icon: '🏆',
        name: `${area.name}制覇`,
        requirement: `${area.name}のすべての県で金メダル`,
        measure: (p) => fixed(medalsIn(p, 'gold'), Math.max(1, inArea(p).length)),
      },
    );
  }

  /* ---- 図鑑 ---- */
  const dexSteps: Array<[number, TrophyTier, string]> = [
    [50, 'bronze', '食べ歩き'],
    [150, 'silver', '食通'],
    [300, 'gold', '郷土の舌'],
  ];
  for (const [n, tier, name] of dexSteps) {
    list.push({
      id: `dex-${n}`,
      group: 'dex',
      tier,
      icon: '📖',
      name,
      requirement: `図鑑を全国で${n}品`,
      measure: (p) => fixed(totalDex(p), n),
    });
  }
  list.push({
    id: 'dex-all',
    group: 'dex',
    tier: 'platinum',
    icon: '📚',
    name: '全国図鑑',
    requirement: '図鑑をすべて埋める',
    measure: (p) => fixed(totalDex(p), Math.max(1, totalDishes(p))),
  });

  /* ---- 料理の種類（7割で制覇） ---- */
  for (const category of FOOD_CATEGORIES) {
    const stat = (p: MetaProgress) => p.categories.get(category.id) ?? { total: 0, found: 0 };
    list.push(
      {
        id: `cat-${category.id}-30`,
        group: 'category',
        tier: 'bronze',
        icon: category.icon,
        name: `${category.name}めぐり`,
        requirement: `全国の${category.name}の3割を図鑑に`,
        measure: (p) => fixed(stat(p).found, ratioTarget(stat(p).total, 0.3)),
      },
      {
        id: `cat-${category.id}-70`,
        group: 'category',
        tier: 'gold',
        icon: category.icon,
        name: `${category.name}制覇`,
        requirement: `全国の${category.name}の7割を図鑑に`,
        measure: (p) => fixed(stat(p).found, ratioTarget(stat(p).total, 0.7)),
      },
    );
  }

  /* ---- 豆知識 ---- */
  list.push(
    {
      id: 'tips-30',
      group: 'tour',
      tier: 'bronze',
      icon: '✨',
      name: '寄り道好き',
      requirement: '豆知識を30本ひらく',
      measure: (p) => fixed(totalTips(p), 30),
    },
    {
      id: 'tips-200',
      group: 'tour',
      tier: 'silver',
      icon: '✨',
      name: 'ものしり',
      requirement: '豆知識を200本ひらく',
      measure: (p) => fixed(totalTips(p), 200),
    },
    {
      id: 'tips-all',
      group: 'tour',
      tier: 'platinum',
      icon: '🗺️',
      name: '歩く観光案内',
      requirement: '豆知識をすべてひらく',
      measure: (p) => fixed(totalTips(p), Math.max(1, allTips(p))),
    },
  );

  /* ---- あそんだ回数 ---- */
  const playSteps: Array<[number, TrophyTier, string]> = [
    [10, 'bronze', '常連'],
    [100, 'silver', '看板客'],
    [500, 'gold', '主（ぬし）'],
  ];
  for (const [n, tier, name] of playSteps) {
    list.push({
      id: `play-${n}`,
      group: 'play',
      tier,
      icon: '🍵',
      name,
      requirement: `ぜんぶで${n}回あそぶ`,
      measure: (p) => fixed(totalPlays(p), n),
    });
  }

  return list;
}

export const TROPHIES: readonly TrophyDefinition[] = build();

export interface TrophyStatus {
  trophy: TrophyDefinition;
  current: number;
  target: number;
  achieved: boolean;
}

export function evaluateTrophies(progress: MetaProgress): TrophyStatus[] {
  return TROPHIES.map((trophy) => {
    const { current, target } = trophy.measure(progress);
    // 全国系で分母が0（県が1つもない）のときは獲得扱いにしない
    const achieved = target > 0 && current >= target && progress.prefs.length > 0;
    return { trophy, current, target, achieved };
  });
}
